import { useNavigate, useParams } from "react-router-dom";
import { useGetCustoms } from "@/api/customs.api";
import { useGetCompany } from "@/api/company.api";
import { useGetPreference } from "@/api/preference.api";
import {
  useGetOneExpedition,
  useUpdateValidatedQuotation,
  useValidateQuotation,
} from "@/api/expedition.api";
import { Loader2 } from "lucide-react";
import {
  GetCustomsResponse,
  GetAllCompanyDto,
  GetOneExpeditionResponseDto,
  GetPreferenceResponse,
} from "@/types/api/api-types";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { CotationUpdateBoFormData } from "@/types/forms/bo/cotationUpdateBoForm.form";
import { DeliveryFormData, DeliveryFormSchema } from "@/types/forms/home/cotationDeliveryForm.form";
import Paths from "@/constants/paths";
import { InfoClients } from "@/components/pendingShipments/finalize/infoClients";
import { InfoBottles } from "@/components/pendingShipments/finalize/infoBottles";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { CotationUpdateBoDefaultValues } from "@/types/forms/bo/cotationUpdateBoForm.form";
import { validateCotationPayload } from "@/components/pendingShipments/finalize/validatedCotationPayload";
import { updateValidatedCotationPayload } from "@/components/pendingShipments/finalize/updateValidatedCotationPayload";
import { ExpeditionProcessStatusEnum } from "@/constants/enums/expeditionProcessStatus.enum";
import { InfoDelivery } from "@/components/pendingShipments/finalize/infoDelivery";

export const UpdateQuotationManualPage = () => {
  const { id } = useParams();
  const { data: expedition, isSuccess: isExpeditionSuccess } = useGetOneExpedition(id || "");
  const { data: customs, isSuccess: isCustomsSuccess } = useGetCustoms();
  const { data: company, isSuccess: isCompanySuccess } = useGetCompany();
  const { data: preference, isSuccess: isPreferenceSuccess } = useGetPreference();

  if (!isExpeditionSuccess || !isCustomsSuccess || !isCompanySuccess || !isPreferenceSuccess)
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-6 p-8">
        <Loader2 className="text-brand700 h-10 w-10 animate-spin" />
      </div>
    );

  return (
    <UpdateQuotationManualForm
      expedition={expedition}
      customs={customs}
      company={company}
      preference={preference}
    />
  );
};

type Props = {
  expedition: GetOneExpeditionResponseDto;
  customs: GetCustomsResponse;
  company: GetAllCompanyDto;
  preference: GetPreferenceResponse;
};

const UpdateQuotationManualForm = ({ expedition, customs, company, preference }: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const { mutateAsync: validateQuotation } = useValidateQuotation();
  const { mutateAsync: updateValidatedQuotation } = useUpdateValidatedQuotation();

  const isValidated = expedition.processStatus !== ExpeditionProcessStatusEnum.PENDING_CUSTOMER;

  const prefPickUpLocation =
    localStorage.getItem("prefPickUpLocation") ||
    company?.pickUpLocations?.find((el) => el.isDefault)?.id ||
    undefined;
  const defaultBillingEntity =
    company?.billingEntities.length === 1 ? company?.billingEntities[0].id : undefined;

  const client =
    expedition?.deliveryInfo?.company ||
    (expedition?.deliveryInfo?.firstName && expedition?.deliveryInfo?.lastName
      ? expedition?.deliveryInfo?.firstName + " " + expedition?.deliveryInfo?.lastName
      : "-");

  const cotationForm = useForm<CotationUpdateBoFormData>({
    defaultValues: CotationUpdateBoDefaultValues({
      expedition,
      prefPickUpLocation,
      defaultBillingEntity,
    }),
    resolver: zodResolver(DeliveryFormSchema),
    mode: "onChange",
  });

  const submitForm: SubmitHandler<DeliveryFormData> = async (data) => {
    if (!isValidated) {
      const payload = validateCotationPayload({
        data,
      });

      const ret = await validateQuotation({
        id: expedition.id,
        payload,
      });

      localStorage.setItem("prefPickUpLocation", data.pickUpLocationId);

      setIsLoading(false);

      if (ret.status === 200 || ret.status === 201) navigate(Paths.home.pendingShipments);
      return;
    }

    const payload = updateValidatedCotationPayload({
      expedition,
      data,
    });

    const ret = await updateValidatedQuotation({
      id: expedition.id,
      payload,
    });

    localStorage.setItem("prefPickUpLocation", data.pickUpLocationId);

    if (ret.status === 200 || ret.status === 201) navigate(Paths.home.validatedShipments);
  };

  return (
    <div className="flex h-fit flex-1 flex-col gap-6 p-4 sm:p-8">
      <div className="flex flex-col gap-8">
        <div className="flex flex-row items-center gap-2">
          {isValidated ? (
            <a
              href={Paths.home.validatedShipments}
              className="text-medium align-middle text-sm text-gray-600"
            >
              Expéditions validées
            </a>
          ) : (
            <a
              href={Paths.home.pendingShipments}
              className="text-medium align-middle text-sm text-gray-600"
            >
              Expéditions en attente
            </a>
          )}
          <span className="align-middle text-[#CBD5E1]">{`>`}</span>
          <h4 className="text-semibold align-middle text-sm text-[#06F]">{client}</h4>
        </div>
        <div className="flex flex-col gap-3">
          <h1 className="text-3xl font-semibold">Expédition basée sur votre formulaire rempli</h1>
        </div>
      </div>
      <FormProvider {...cotationForm}>
        <form
          onSubmit={cotationForm.handleSubmit(submitForm)}
          className="flex flex-1 flex-col items-center gap-6"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
            }
          }}
        >
          <InfoClients
            customs={customs}
            selectedCustomId={expedition?.quotation?.customId || ""}
            destinationCountry={expedition?.deliveryInfo?.country || ""}
            hasCompanyName={!!expedition?.deliveryInfo?.company}
          />
          <InfoBottles preference={preference} />
          <InfoDelivery
            pickUpLocations={company?.pickUpLocations || []}
            billingEntities={company?.billingEntities || []}
            documents={expedition?.expeditionDocuments?.documents || []}
          />
          <div className="flex w-full flex-row gap-3 md:max-w-[1000px] md:flex-row">
            <Button
              disabled={isLoading}
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() =>
                navigate(isValidated ? Paths.home.validatedShipments : Paths.home.pendingShipments)
              }
            >
              Annuler
            </Button>
            <Button
              disabled={isLoading}
              onClick={() => cotationForm.handleSubmit(submitForm)}
              type="submit"
              className="flex-1"
            >
              {isLoading ? (
                <Loader2 className="animate-spin" />
              ) : isValidated ? (
                "Modifier l'expédition"
              ) : (
                "Valider l'expédition"
              )}
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};
